import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import EditJobModal from '../EditJobModal';
import AddJobModal from '../custom-jobs/AddJobModal';
import { SEND_EMAIL_ALERT, SEND_TEXT_ALERT } from '../../graphql/mutations';

const JobAdminBar = ({ job, onJobUpdate }) => {
  const { user } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateJobModalOpen, setIsCreateJobModalOpen] = useState(false);

  // GraphQL mutations
  const [sendEmailAlert, { loading: isEmailAlertLoading }] = useMutation(SEND_EMAIL_ALERT, {
    onCompleted: (data) => {
      if (data.sendEmailAlert) {
        toast.success(`Job assignment email sent to ${job.assignedTo?.name || job.assignedTo?.email}!`);
      } else {
        toast.error('Failed to send email alert');
      }
    },
    onError: (error) => {
      console.error('Email alert error:', error);
      toast.error(`Failed to send email alert: ${error.message}`);
    }
  });

  const [sendTextAlert, { loading: isTextAlertLoading }] = useMutation(SEND_TEXT_ALERT, {
    onCompleted: (data) => {
      if (data.sendTextAlert) {
        toast.success(`Job assignment text sent to ${job.assignedTo?.phoneNumber}!`);
      } else {
        toast.error('Failed to send text alert');
      }
    },
    onError: (error) => {
      console.error('Text alert error:', error);
      toast.error(`Failed to send text alert: ${error.message}`);
    }
  });

  // Check if user has required role
  const hasRequiredRole = user && ['ADMIN', 'GUEST_APP_MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER'].includes(user.role);

  // Don't render if user doesn't have required role
  if (!hasRequiredRole) {
    return null;
  }

  const handleEditJob = () => {
    setIsEditModalOpen(true);
  };

  const handleCreateJob = () => {
    setIsCreateJobModalOpen(true);
  };

  const handleSendEmailAlert = () => {
    if (!job.assignedTo) {
      toast.error('This job is not assigned to any user');
      return;
    }

    sendEmailAlert({
      variables: {
        jobId: job.id
      }
    });
  };

  const handleSendTextAlert = () => {
    if (!job.assignedTo) {
      toast.error('This job is not assigned to any user');
      return;
    }

    if (!job.assignedTo.phoneNumber) {
      toast.error(`${job.assignedTo.name || 'The assigned user'} doesn't have a phone number on file`);
      return;
    }

    sendTextAlert({
      variables: {
        jobId: job.id
      }
    });
  };

  const handleEditJobSuccess = () => {
    setIsEditModalOpen(false);
    if (onJobUpdate) {
      onJobUpdate();
    }
    toast.success('Job updated successfully!');
  };

  const handleCreateJobSuccess = () => {
    setIsCreateJobModalOpen(false);
    toast.success('Job created successfully!');
  };

  return (
    <>
      {/* Admin Actions Bar */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3 lg:mb-6">
        {/* Header */}
        <div className="flex items-center space-x-2 mb-4">
          <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.349 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.349a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.349 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.349a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h3 className="text-sm font-medium text-blue-900">Admin Actions</h3>
        </div>
        
        {/* Action Buttons - Mobile: 2x2 grid, Desktop: horizontal row */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3">
          {/* Edit Job Button */}
          <button
            onClick={handleEditJob}
            className="inline-flex items-center justify-center px-3 py-2 border border-blue-300 text-xs font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors sm:justify-start"
          >
            <svg className="h-4 w-4 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="hidden sm:inline">Edit Job & Reservation</span>
            <span className="sm:hidden text-center">Edit Job</span>
          </button>

          {/* Email Alert Button */}
          <button
            onClick={handleSendEmailAlert}
            disabled={!job.assignedTo || isEmailAlertLoading}
            className="inline-flex items-center justify-center px-3 py-2 border border-green-300 text-xs font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed sm:justify-start"
          >
            <svg className="h-4 w-4 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="hidden sm:inline">{isEmailAlertLoading ? 'Sending...' : 'Send Email Alert'}</span>
            <span className="sm:hidden text-center">{isEmailAlertLoading ? 'Sending...' : 'Email'}</span>
          </button>

          {/* Text Alert Button */}
          <button
            onClick={handleSendTextAlert}
            disabled={!job.assignedTo || !job.assignedTo.phoneNumber || isTextAlertLoading}
            className="inline-flex items-center justify-center px-3 py-2 border border-yellow-300 text-xs font-medium rounded-md text-yellow-700 bg-white hover:bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed sm:justify-start"
          >
            <svg className="h-4 w-4 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="hidden sm:inline">{isTextAlertLoading ? 'Sending...' : 'Send Text Alert'}</span>
            <span className="sm:hidden text-center">{isTextAlertLoading ? 'Sending...' : 'Text'}</span>
          </button>

          {/* Create Job Button */}
          <button
            onClick={handleCreateJob}
            className="inline-flex items-center justify-center px-3 py-2 border border-purple-300 text-xs font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors sm:justify-start"
          >
            <svg className="h-4 w-4 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="hidden sm:inline">Create Job</span>
            <span className="sm:hidden text-center">Create</span>
          </button>
        </div>

        {/* Assignment Status */}
        {!job.assignedTo && (
          <div className="mt-3 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
            ⚠️ This job is not assigned to any user. Email and text alerts are disabled.
          </div>
        )}
        
        {job.assignedTo && !job.assignedTo.phoneNumber && (
          <div className="mt-3 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
            ⚠️ Assigned user has no phone number. Text alerts are disabled.
          </div>
        )}
      </div>

      {/* Edit Job Modal */}
      {isEditModalOpen && (
        <EditJobModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          job={job}
          onJobUpdate={handleEditJobSuccess}
        />
      )}

      {/* Create Job Modal */}
      {isCreateJobModalOpen && (
        <AddJobModal
          isOpen={isCreateJobModalOpen}
          onClose={() => setIsCreateJobModalOpen(false)}
          onCreateJob={handleCreateJobSuccess}
          initialPropertyId={job.property?.id}
          initialProperty={job.property}
        />
      )}
    </>
  );
};

export default JobAdminBar;
